import { useCallback } from "react";

import { getAreaGeoContext } from "./aiMappingDetections";
import { handleAiMappingMutationFailure } from "./aiMappingControllerUtils";
import {
  erasePixelPolygons,
  geoPointsToPixelPolygon,
  pixelPolygonArea,
  pixelPolygonsTouch,
  splitPixelPolygonByLine,
  unionPixelPolygons,
} from "./aiMappingGeometry";
import { LatLngPair, geoToPixel } from "./aiMappingGeo";
import { AiMappingActiveTask, AiMappingNotify } from "./aiMappingControllerTypes";
import { unwrap } from "./aiMappingControllerUtils";
import { AiMappingAreaLike, AiMappingMapDriver, AiMappingResultsLike } from "./aiMappingTypes";
import {
  useCreateAiMappingSegmentation,
  useDeleteAiMappingSegmentation,
  useGetAiMappingRunAreaResults,
  useUpdateAiMappingSegmentation,
} from "./hooks";

interface UseAiMappingSegmentationHandlersOptions {
  busy: boolean;
  activeTask: AiMappingActiveTask | null;
  allResults: AiMappingResultsLike[];
  unmountedRef: React.MutableRefObject<boolean>;
  setBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedSegmentationId: React.Dispatch<React.SetStateAction<string | null>>;
  setCheckedSegmentationIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSplittingSegmentationId: React.Dispatch<React.SetStateAction<string | null>>;
  setAddingSegmentationAreaId: React.Dispatch<React.SetStateAction<string | null>>;
  applyResults: (payload: AiMappingResultsLike) => void;
  refetchResults: (areaId: string) => Promise<void> | Promise<AiMappingResultsLike | null>;
  getAreaById: (areaId: string) => AiMappingAreaLike | null;
  driver: AiMappingMapDriver | null;
  notify: AiMappingNotify;
  handleError: (error: unknown, options?: { inline?: boolean }) => boolean;
}

export function useAiMappingSegmentationHandlers({
  busy,
  activeTask,
  allResults,
  unmountedRef,
  setBusy,
  setSelectedSegmentationId,
  setCheckedSegmentationIds,
  setSplittingSegmentationId,
  setAddingSegmentationAreaId,
  applyResults,
  refetchResults,
  getAreaById,
  driver,
  notify,
  handleError,
}: UseAiMappingSegmentationHandlersOptions) {
  const [updateSegmentationAction] = useUpdateAiMappingSegmentation();
  const [deleteSegmentationAction] = useDeleteAiMappingSegmentation();
  const [createSegmentationAction] = useCreateAiMappingSegmentation();
  const [fetchResultsQuery] = useGetAiMappingRunAreaResults();

  const selectSegmentation = useCallback(
    (segmentationId: string | null) => {
      setSelectedSegmentationId(segmentationId);
    },
    [setSelectedSegmentationId]
  );

  const toggleSegmentationChecked = useCallback(
    (segmentationId: string) => {
      setCheckedSegmentationIds((current) =>
        current.includes(segmentationId)
          ? current.filter((id) => id !== segmentationId)
          : [...current, segmentationId]
      );
    },
    [setCheckedSegmentationIds]
  );

  const updateSegmentation = useCallback(
    async (
      areaId: string,
      segmentationId: string,
      changes: { category?: string; color?: string | null; isDeleted?: boolean }
    ) => {
      setBusy(true);
      const result = await updateSegmentationAction({
        variables: {
          input: {
            runAreaId: areaId,
            segmentationId,
            category: changes.category,
            color: changes.color ?? null,
            isDeleted: changes.isDeleted,
          },
        },
      });
      if (!result.success) {
        await handleAiMappingMutationFailure(result, {
          notify,
          handleError,
          unmountedRef,
          staleCodes: ["segmentation_not_found"],
          onStale: async () => {
            await refetchResults(areaId);
          },
        });
      } else {
        const bundle = result.data?.updateAiMappingSegmentation as AiMappingResultsLike | undefined;
        if (bundle && !unmountedRef.current) {
          applyResults(bundle);
          notify("Area updated", "success");
        }
      }
      if (!unmountedRef.current) setBusy(false);
    },
    [
      busy,
      updateSegmentationAction,
      applyResults,
      refetchResults,
      handleError,
      notify,
      setBusy,
      unmountedRef,
    ]
  );

  const deleteSegmentation = useCallback(
    async (areaId: string, segmentationId: string) => {
      if (busy) return;
      setBusy(true);
      const result = await deleteSegmentationAction({
        variables: { runAreaId: areaId, segmentationId },
      });
      if (!result.success) {
        await handleAiMappingMutationFailure(result, {
          notify,
          handleError,
          unmountedRef,
          staleCodes: ["segmentation_not_found"],
          onStale: async () => {
            await refetchResults(areaId);
          },
        });
      } else {
        const bundle = result.data?.deleteAiMappingSegmentation as AiMappingResultsLike | undefined;
        if (bundle && !unmountedRef.current) {
          applyResults(bundle);
          setSelectedSegmentationId(null);
          setCheckedSegmentationIds((current) => current.filter((id) => id !== segmentationId));
          notify("Area deleted", "success");
        }
      }
      if (!unmountedRef.current) setBusy(false);
    },
    [
      busy,
      deleteSegmentationAction,
      applyResults,
      refetchResults,
      handleError,
      notify,
      setBusy,
      setSelectedSegmentationId,
      setCheckedSegmentationIds,
      unmountedRef,
    ]
  );

  const mergeSegmentations = useCallback(
    async (areaId: string, segmentationIds: string[]) => {
      if (busy || segmentationIds.length < 2) return;
      const areaResults = allResults.find(
        (areaResult) => String(areaResult.area.id) === String(areaId)
      );
      const targets = (areaResults?.segmentations ?? []).filter(
        (segmentation) =>
          !segmentation.isDeleted && segmentationIds.includes(String(segmentation.id))
      );
      if (targets.length < 2) {
        notify("Select at least two areas to merge.", "warning");
        return;
      }
      const categories = new Set(targets.map((target) => target.category));
      if (categories.size > 1) {
        notify("Only areas of the same category can be merged.", "warning");
        return;
      }
      setBusy(true);
      try {
        const merged = unionPixelPolygons(targets.map((target) => target.polygon));
        if (!merged) {
          notify("Selected areas do not touch, so they cannot be merged.", "warning");
          return;
        }
        const [primary, ...absorbed] = targets;
        const updateResult = await updateSegmentationAction({
          variables: {
            input: { runAreaId: areaId, segmentationId: String(primary.id), polygon: merged },
          },
        });
        if (!updateResult.success) throw updateResult;
        for (const segmentation of absorbed) {
          const deleteResult = await deleteSegmentationAction({
            variables: { runAreaId: areaId, segmentationId: String(segmentation.id) },
          });
          if (!deleteResult.success) throw deleteResult;
        }
        const payload = unwrap(
          await fetchResultsQuery({ variables: { id: areaId }, fetchPolicy: "network-only" }),
          (data) => data?.getAiMappingRunAreaResults
        ) as AiMappingResultsLike;
        if (!unmountedRef.current) {
          applyResults(payload);
          setCheckedSegmentationIds([]);
          setSelectedSegmentationId(null);
          notify("Areas merged", "success");
        }
      } catch (error) {
        if (!unmountedRef.current) handleError(error);
      } finally {
        if (!unmountedRef.current) setBusy(false);
      }
    },
    [
      busy,
      allResults,
      updateSegmentationAction,
      deleteSegmentationAction,
      fetchResultsQuery,
      applyResults,
      handleError,
      notify,
      setBusy,
      setCheckedSegmentationIds,
      setSelectedSegmentationId,
      unmountedRef,
    ]
  );

  const splitSegmentation = useCallback(
    (areaId: string, segmentationId: string) => {
      if (busy || activeTask) return;
      const area = getAreaById(areaId);
      const context = getAreaGeoContext(area);
      const areaResults = allResults.find(
        (areaResult) => String(areaResult.area.id) === String(areaId)
      );
      const target = (areaResults?.segmentations ?? []).find(
        (segmentation) =>
          !segmentation.isDeleted && String(segmentation.id) === String(segmentationId)
      );
      if (!target || !context.imageWidth || !context.imageHeight || !context.bounds) {
        notify("Run detection on this area first before editing areas.", "warning");
        return;
      }
      setSplittingSegmentationId(segmentationId);
      notify("Draw a line across the area to split it.", "info");
      driver?.startDraw(
        "LINE",
        (points) => {
          setSplittingSegmentationId(null);
          if (points.length < 2) return;
          const pixelPoints = points
            .map(([lat, lng]) =>
              geoToPixel(lat, lng, context.imageWidth!, context.imageHeight!, context.bounds!)
            )
            .filter((point): point is [number, number] => !!point);
          if (pixelPoints.length < 2) {
            notify("Could not map the drawn line onto the image.", "error");
            return;
          }
          const start = pixelPoints[0];
          const end = pixelPoints[pixelPoints.length - 1];
          const pieces = splitPixelPolygonByLine(target.polygon, start, end);
          if (!pieces) {
            notify("That line does not divide the area into two pieces.", "warning");
            return;
          }
          const [firstPiece, secondPiece] = pieces;
          void (async () => {
            setBusy(true);
            try {
              const updateResult = await updateSegmentationAction({
                variables: {
                  input: {
                    runAreaId: areaId,
                    segmentationId: String(target.id),
                    polygon: firstPiece,
                  },
                },
              });
              if (!updateResult.success) throw updateResult;
              const createResult = await createSegmentationAction({
                variables: {
                  input: {
                    runAreaId: areaId,
                    category: target.category,
                    polygon: secondPiece,
                    color: target.color ?? null,
                  },
                },
              });
              if (!createResult.success) throw createResult;
              const payload = unwrap(
                await fetchResultsQuery({ variables: { id: areaId }, fetchPolicy: "network-only" }),
                (data) => data?.getAiMappingRunAreaResults
              ) as AiMappingResultsLike;
              if (!unmountedRef.current) {
                applyResults(payload);
                notify("Area split", "success");
              }
            } catch (error) {
              if (!unmountedRef.current) handleError(error);
            } finally {
              if (!unmountedRef.current) setBusy(false);
            }
          })();
        },
        () => setSplittingSegmentationId(null)
      );
    },
    [
      busy,
      activeTask,
      getAreaById,
      allResults,
      driver,
      updateSegmentationAction,
      createSegmentationAction,
      fetchResultsQuery,
      applyResults,
      notify,
      handleError,
      setBusy,
      setSplittingSegmentationId,
      unmountedRef,
    ]
  );

  const splitAreasAlongLine = useCallback(
    async (line: LatLngPair[], targetSegmentationId?: string | null): Promise<number> => {
      if (busy || activeTask) return 0;
      if (line.length < 2) {
        notify("Draw a line with at least two points across a measured area.", "info");
        return 0;
      }
      setBusy(true);
      let splitCount = 0;
      try {
        const changedAreas = new Set<string>();
        for (const areaResult of allResults) {
          const area = getAreaById(areaResult.area.id);
          const context = getAreaGeoContext(area);
          if (!context.imageWidth || !context.imageHeight || !context.bounds) continue;
          const pixelLine: [number, number][] = [];
          for (const [lat, lng] of line.slice(0, 2)) {
            const pixel = geoToPixel(
              lat,
              lng,
              context.imageWidth,
              context.imageHeight,
              context.bounds
            );
            if (pixel) pixelLine.push(pixel);
          }
          if (pixelLine.length < 2) continue;

          for (const segmentation of areaResult.segmentations.filter(
            (segmentation) =>
              !segmentation.isDeleted &&
              (!targetSegmentationId || String(segmentation.id) === String(targetSegmentationId))
          )) {
            const pieces = splitPixelPolygonByLine(
              segmentation.polygon,
              pixelLine[0],
              pixelLine[1]
            );
            if (!pieces) continue;

            await updateSegmentationAction({
              variables: {
                input: {
                  runAreaId: areaResult.area.id,
                  segmentationId: String(segmentation.id),
                  polygon: pieces[0],
                },
              },
            });
            await createSegmentationAction({
              variables: {
                input: {
                  runAreaId: areaResult.area.id,
                  category: segmentation.category,
                  polygon: pieces[1],
                  color: segmentation.color ?? null,
                },
              },
            });
            changedAreas.add(areaResult.area.id);
            splitCount += 1;
          }
        }
        for (const areaId of changedAreas) await refetchResults(areaId);
        return splitCount;
      } catch (error) {
        if (!unmountedRef.current) handleError(error);
        return splitCount;
      } finally {
        if (!unmountedRef.current) setBusy(false);
      }
    },
    [
      busy,
      activeTask,
      allResults,
      getAreaById,
      updateSegmentationAction,
      createSegmentationAction,
      refetchResults,
      notify,
      handleError,
      setBusy,
      unmountedRef,
    ]
  );

  const eraseAreasAlongPolygon = useCallback(
    async (points: LatLngPair[]): Promise<number> => {
      if (busy || activeTask) return 0;
      if (points.length < 3) return 0;
      setBusy(true);
      let erasedCount = 0;
      let trimmedCount = 0;
      try {
        const changedAreas = new Set<string>();
        for (const areaResult of allResults) {
          const area = getAreaById(areaResult.area.id);
          const context = getAreaGeoContext(area);
          if (!context.imageWidth || !context.imageHeight || !context.bounds) continue;
          const erasePolygon = geoPointsToPixelPolygon(
            points,
            context.imageWidth,
            context.imageHeight,
            context.bounds
          );
          if (!erasePolygon) continue;

          for (const segmentation of areaResult.segmentations.filter(
            (segmentation) => !segmentation.isDeleted
          )) {
            const result = erasePixelPolygons(segmentation.polygon, erasePolygon);
            if (!result) continue;

            if (result.fullyErased) {
              const deleteResult = await deleteSegmentationAction({
                variables: {
                  runAreaId: areaResult.area.id,
                  segmentationId: String(segmentation.id),
                },
              });
              if (!deleteResult.success) throw deleteResult;
              erasedCount += 1;
              changedAreas.add(areaResult.area.id);
              continue;
            }

            const [primary, ...extraPieces] = result.pieces;
            const updateResult = await updateSegmentationAction({
              variables: {
                input: {
                  runAreaId: areaResult.area.id,
                  segmentationId: String(segmentation.id),
                  polygon: primary,
                },
              },
            });
            if (!updateResult.success) throw updateResult;
            for (const piece of extraPieces) {
              const createResult = await createSegmentationAction({
                variables: {
                  input: {
                    runAreaId: areaResult.area.id,
                    category: segmentation.category,
                    polygon: piece,
                    color: segmentation.color ?? null,
                  },
                },
              });
              if (!createResult.success) throw createResult;
            }
            trimmedCount += 1;
            changedAreas.add(areaResult.area.id);
          }
        }
        for (const areaId of changedAreas) await refetchResults(areaId);

        if (erasedCount === 0 && trimmedCount === 0) return 0;
        if (erasedCount > 0 && trimmedCount === 0) {
          notify(`Deleted ${erasedCount} area${erasedCount === 1 ? "" : "s"}`, "success");
        } else if (trimmedCount > 0 && erasedCount === 0) {
          notify(`Trimmed ${trimmedCount} area${trimmedCount === 1 ? "" : "s"}`, "success");
        } else {
          notify(`Erased ${erasedCount + trimmedCount} areas`, "success");
        }
        return erasedCount + trimmedCount;
      } catch (error) {
        if (!unmountedRef.current) handleError(error);
        return erasedCount + trimmedCount;
      } finally {
        if (!unmountedRef.current) setBusy(false);
      }
    },
    [
      busy,
      activeTask,
      allResults,
      getAreaById,
      updateSegmentationAction,
      createSegmentationAction,
      deleteSegmentationAction,
      refetchResults,
      notify,
      handleError,
      setBusy,
      unmountedRef,
    ]
  );

  const mergeAreasAlongPolygon = useCallback(
    async (points: LatLngPair[]): Promise<number> => {
      if (busy || activeTask) return 0;
      if (points.length < 3) {
        notify("Draw a polygon around the measured areas to merge them.", "info");
        return 0;
      }
      setBusy(true);
      let mergedCount = 0;
      try {
        const changedAreas = new Set<string>();
        for (const areaResult of allResults) {
          const area = getAreaById(areaResult.area.id);
          const context = getAreaGeoContext(area);
          if (!context.imageWidth || !context.imageHeight || !context.bounds) continue;
          const mergePolygon = geoPointsToPixelPolygon(
            points,
            context.imageWidth,
            context.imageHeight,
            context.bounds
          );
          if (!mergePolygon) continue;

          const touching = areaResult.segmentations.filter(
            (segmentation) =>
              !segmentation.isDeleted && pixelPolygonsTouch(segmentation.polygon, mergePolygon)
          );
          if (touching.length === 0) continue;

          const unioned = unionPixelPolygons([
            mergePolygon,
            ...touching.map((target) => target.polygon),
          ]);
          if (!unioned) continue;

          const primary = touching.reduce((a, b) =>
            pixelPolygonArea(b.polygon) > pixelPolygonArea(a.polygon) ? b : a
          );
          const updateResult = await updateSegmentationAction({
            variables: {
              input: {
                runAreaId: areaResult.area.id,
                segmentationId: String(primary.id),
                polygon: unioned,
              },
            },
          });
          if (!updateResult.success) throw updateResult;
          for (const absorbed of touching) {
            if (String(absorbed.id) === String(primary.id)) continue;
            const deleteResult = await deleteSegmentationAction({
              variables: {
                runAreaId: areaResult.area.id,
                segmentationId: String(absorbed.id),
              },
            });
            if (!deleteResult.success) throw deleteResult;
          }
          mergedCount += touching.length;
          changedAreas.add(areaResult.area.id);
        }
        for (const areaId of changedAreas) await refetchResults(areaId);
        return mergedCount;
      } catch (error) {
        if (!unmountedRef.current) handleError(error);
        return mergedCount;
      } finally {
        if (!unmountedRef.current) setBusy(false);
      }
    },
    [
      busy,
      activeTask,
      allResults,
      getAreaById,
      updateSegmentationAction,
      deleteSegmentationAction,
      refetchResults,
      notify,
      handleError,
      setBusy,
      unmountedRef,
    ]
  );

  const startAddSegmentation = useCallback(
    (areaId: string) => {
      const area = getAreaById(areaId);
      const context = getAreaGeoContext(area);
      if (!context.imageWidth || !context.imageHeight || !context.bounds) {
        notify("Run detection on this area first before adding an area.", "warning");
        return;
      }
      setAddingSegmentationAreaId(areaId);
      driver?.startDraw("POLYGON", (points) => {
        setAddingSegmentationAreaId(null);
        const polygon = geoPointsToPixelPolygon(
          points,
          context.imageWidth!,
          context.imageHeight!,
          context.bounds!
        );
        if (!polygon) {
          notify("Could not map the drawn area onto the image.", "error");
          return;
        }
        void (async () => {
          setBusy(true);
          const result = await createSegmentationAction({
            variables: { input: { runAreaId: areaId, polygon } },
          });
          if (result.success) {
            const bundle = result.data?.createAiMappingSegmentation as
              | AiMappingResultsLike
              | undefined;
            if (bundle && !unmountedRef.current) {
              applyResults(bundle);
              notify("Area added", "success");
            }
          } else if (!unmountedRef.current) {
            handleError(result);
          }
          if (!unmountedRef.current) setBusy(false);
        })();
      });
    },
    [
      getAreaById,
      driver,
      createSegmentationAction,
      applyResults,
      notify,
      handleError,
      setBusy,
      setAddingSegmentationAreaId,
      unmountedRef,
    ]
  );

  return {
    selectSegmentation,
    toggleSegmentationChecked,
    updateSegmentation,
    deleteSegmentation,
    mergeSegmentations,
    splitSegmentation,
    splitAreasAlongLine,
    eraseAreasAlongPolygon,
    mergeAreasAlongPolygon,
    startAddSegmentation,
  };
}
